import { withReferralParam } from '@/lib/utils/referralCode';

export type SocialShareTarget = 'x' | 'facebook' | 'whatsapp' | 'telegram';

/**
 * Absolutize a possibly-relative invite link (the API returns paths like "/r/abc")
 * against the current origin, so shared URLs and the card QR are openable.
 */
export function absolutizeReferralLink(raw: string): string {
  if (!raw || raw.startsWith('http') || typeof window === 'undefined') return raw;
  return new URL(raw, window.location.origin).href;
}

/**
 * Build a platform composer URL. These are share *intents*: they carry a link +
 * text, never an image file (the picture goes via Download). Facebook's sharer
 * accepts only a URL; WhatsApp only a text field, so the link is folded into it.
 */
export function socialShareUrl(target: SocialShareTarget, link: string, text: string): string {
  const url = encodeURIComponent(link);
  const msg = encodeURIComponent(text);
  switch (target) {
    case 'x':
      return `https://twitter.com/intent/tweet?url=${url}&text=${msg}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`;
    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${msg}`;
  }
}

interface ProfileShareLinkInput {
  origin: string;
  locale: string;
  /** The profile's segment as the page address carries it (`params.username`). */
  segment: string;
  /** The sharer's own referral code; without one the link carries no `?ref=`. */
  referralCode: string | undefined;
}

/**
 * A player's profile as a share link: absolute, in the sharer's locale, and
 * with the sharer's `?ref=` — whoever's profile it is — so a guest who signs
 * up from it becomes the sharer's referral.
 */
export function profileShareLink({
  origin,
  locale,
  segment,
  referralCode,
}: ProfileShareLinkInput): string {
  const url = new URL(`/${locale}/stats/profile/${encodeURIComponent(segment)}`, origin);
  return withReferralParam(url.href, referralCode);
}

/** Open a platform share composer in a small browser popup window. */
export function openSharePopup(url: string): void {
  window.open(url, '_blank', 'width=600,height=520,noopener,noreferrer');
}
